import { Provider } from '@angular/core';
import {
  provideLucideIcons, LucideIcon, LucideIconData,
  LucideClock, LucidePlus, LucideMapPinPlus, LucideSmilePlus, LucideShieldCheck,
  LucideBusFront, LucideArrowLeft, LucidePaperclip, LucideSparkles, LucideWandSparkles,
  LucideIdCard, LucideZap, LucideWrench, LucideBuilding, LucideRadioTower, LucideCheck,
  LucideCircleCheck, LucideChevronLeft, LucideChevronRight, LucideX, LucideCopy,
  LucideBuilding2, LucideTrash2, LucideFileText, LucideMonitorSmartphone, LucideCar,
  LucideGripVertical, LucideSquarePen, LucideChevronDown, LucideFunnel, LucideFunnelX,
  LucideMessagesSquare, LucideUsers, LucideHouse, LucideHourglass, LucideNetwork,
  LucideLaptop, LucideLayers, LucideTruck, LucideMedal, LucideLocateFixed, LucideNavigation,
  LucideExternalLink, LucideUser, LucideUserPlus, LucideUserMinus, LucidePlay, LucideLoaderCircle, LucideCircle,
  LucideRotateCcw, LucideCaravan, LucideSend, LucideSettings, LucideShield, LucideWifiOff,
  LucideSmartphone, LucideCircleStop, LucideStore, LucideTablet, LucideSlidersHorizontal,
  LucideHeartHandshake, LucideWatch, LucideLandmark, LucideGlobe, LucideCornerDownRight,
  LucideFolder, LucideSquareCheckBig, LucideSquare, LucideStar,
  LucideSearch, LucideSearchX, LucideArrowDownUp, LucideCircleX, LucideShieldAlert,
  LucideRefreshCw, LucideEye, LucideBinoculars,
  LucideTicket, LucideTicketPlus, LucideTicketCheck, LucideTicketX, LucideKeyRound,
  LucideMapPin, LucideRoute, LucideFlag, LucideHistory, LucideCircleAlert, LucideFileUp,
  LucideMenu, LucideMessageCircleMore,
} from '@lucide/angular';

/**
 * Bridges the app's existing Material Icons ligature names to lucide icon components. Every
 * `<svg lucideIcon="…">` and dynamic `[lucideIcon]` binding — including the names emitted by
 * model constants (ROLE_ICON, REALM_TYPE_ICON, TEAM_MEMBER_ICONS, DEVICE_ICON, orgTypeIcon)
 * — resolves through the registration below, so no binding or model needed to change to swap
 * the look.
 *
 * Scope: the Angular UI only. Mapbox marker/popup HTML still renders via the Material Icons
 * webfont, so that font stays loaded and these Material names keep working there too.
 */
// NOTE: keys are the PascalCase form of the Material name; they are converted to the Material
// ligature (snake_case) below and registered as the lucide icon's lookup name. lucide's
// LucideDynamicIcon does an EXACT lookup and THROWS on a miss, so `lucideIcon="admin_panel_settings"`
// resolves via key `AdminPanelSettings` → name `admin_panel_settings`.
const ICONS: Record<string, LucideIcon> = {
  AccessTime: LucideClock,
  Schedule: LucideClock,
  Add: LucidePlus,
  AddLocation: LucideMapPinPlus,
  AddReaction: LucideSmilePlus,
  AdminPanelSettings: LucideShieldCheck,
  AirportShuttle: LucideBusFront,
  ArrowBack: LucideArrowLeft,
  AttachFile: LucidePaperclip,
  AutoAwesome: LucideSparkles,
  AutoFixHigh: LucideWandSparkles,
  Badge: LucideIdCard,
  Bolt: LucideZap,
  Build: LucideWrench,
  Business: LucideBuilding,
  CellTower: LucideRadioTower,
  Check: LucideCheck,
  CheckCircle: LucideCircleCheck,
  ChevronLeft: LucideChevronLeft,
  ChevronRight: LucideChevronRight,
  Close: LucideX,
  ContentCopy: LucideCopy,
  CorporateFare: LucideBuilding2,
  Delete: LucideTrash2,
  DeleteOutline: LucideTrash2,
  DeleteSweep: LucideTrash2,
  Description: LucideFileText,
  Devices: LucideMonitorSmartphone,
  DevicesOther: LucideMonitorSmartphone,
  DirectionsCar: LucideCar,
  Domain: LucideBuilding2,
  DragIndicator: LucideGripVertical,
  Edit: LucideSquarePen,
  ExpandMore: LucideChevronDown,
  FilterAlt: LucideFunnel,
  FilterAltOff: LucideFunnelX,
  Forum: LucideMessagesSquare,
  Group: LucideUsers,
  Groups: LucideUsers,
  Home: LucideHouse,
  HourglassEmpty: LucideHourglass,
  Hub: LucideNetwork,
  Laptop: LucideLaptop,
  Layers: LucideLayers,
  LocalShipping: LucideTruck,
  MilitaryTech: LucideMedal,
  MyLocation: LucideLocateFixed,
  NearMe: LucideNavigation,
  OpenInNew: LucideExternalLink,
  Person: LucideUser,
  PersonAdd: LucideUserPlus,
  PersonRemove: LucideUserMinus,
  PlayArrow: LucidePlay,
  ProgressActivity: LucideLoaderCircle,
  RadioButtonUnchecked: LucideCircle,
  RestartAlt: LucideRotateCcw,
  RvHookup: LucideCaravan,
  Send: LucideSend,
  Settings: LucideSettings,
  Shield: LucideShield,
  SignalWifiOff: LucideWifiOff,
  Smartphone: LucideSmartphone,
  StopCircle: LucideCircleStop,
  Store: LucideStore,
  TabletAndroid: LucideTablet,
  Tune: LucideSlidersHorizontal,
  VolunteerActivism: LucideHeartHandshake,
  Watch: LucideWatch,
  AccountBalance: LucideLandmark,
  Language: LucideGlobe,
  SubdirectoryArrowRight: LucideCornerDownRight,
  Folder: LucideFolder,
  CheckBox: LucideSquareCheckBig,
  CheckBoxOutlineBlank: LucideSquare,
  Star: LucideStar,
  StarBorder: LucideStar,
  Search: LucideSearch,
  SearchOff: LucideSearchX,
  Sort: LucideArrowDownUp,
  Cancel: LucideCircleX,
  // Safety-admin marker (Material 3 "policy_alert"): a shield with an alert glyph.
  PolicyAlert: LucideShieldAlert,
  Policy: LucideShieldAlert,
  Sync: LucideRefreshCw,
  // "who can see whom" location-visibility view.
  Visibility: LucideEye,
  // Single-track focus indicator on the map.
  Binoculars: LucideBinoculars,
  // Invite feature: ticket for invites, ticket_plus to create, ticket_check accepted, ticket_x expired.
  Ticket: LucideTicket,
  TicketPlus: LucideTicketPlus,
  TicketCheck: LucideTicketCheck,
  TicketX: LucideTicketX,
  // Accept-an-invite-code entry points (matches the account-recovery overlay's Material "vpn_key").
  VpnKey: LucideKeyRound,
  // HUD section headers: Monitored Locations / Routing / Operational Pins.
  Pin: LucideMapPin,
  Route: LucideRoute,
  Flag: LucideFlag,
  // Admin Audit tab: History for the tab/row icon, Error for failed-entry status.
  History: LucideHistory,
  Error: LucideCircleAlert,
  // Accounts view "Import CSV" button.
  UploadFile: LucideFileUp,
  // Mobile HUD collapse/expand toggle (paired with `Close` for the open state).
  Menu: LucideMenu,
  // Team Alerts: "Create Team Alert" entry points (row action, detail dialog, HUD, map menu).
  Sms: LucideMessageCircleMore,
};

/** `CorporateFare` → `corporate_fare` (the Material ligature used as the lucide lookup name). */
const toMaterialName = (pascal: string): string =>
  pascal.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/**
 * App-config provider that registers the Material-name → lucide icon map. Each lucide icon is
 * re-registered under its Material ligature name so existing `lucideIcon="…"` strings resolve.
 */
export const lucideIconsProvider: Provider = provideLucideIcons(
  ...Object.entries(ICONS).map(
    ([pascal, comp]): LucideIconData => ({ ...comp.icon, name: toMaterialName(pascal) }),
  ),
);
